// useStatusShortcuts — keyboard bindings for the Command Center StatusPill.
//
// Wires up single-key status transitions while a user is viewing a thread:
//   O   → open
//   A   → awaiting
//   R   → resolved
//   !   → spam          (also Shift+1)
//   Z   → open the snooze menu (parent handles popover state)
//
// Deliberately skipped when the user is typing (Composer textarea, any input,
// contentEditable) so the shortcuts don't collide with message drafting.
// Also skipped when any modifier besides Shift is held.
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConversationStatus } from '@md/shared';
import { useUpdateStatus } from '@/lib/hooks/useConversation';
type Args = {
  conversationId: string | undefined;
  enabled: boolean;
  /** Parent owns the SnoozeMenu visibility; Z just asks it to open. */
  onSnoozeRequest: () => void;
};
const KEY_MAP: Record<string, ConversationStatus | 'snooze'> = {
  o: 'open',
  a: 'awaiting',
  r: 'resolved',
  '!': 'spam',
  '1': 'spam', // Shift+1 on US layout
  z: 'snooze',
};
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  const role = target.getAttribute('role');
  if (role === 'menu' || role === 'menuitem' || role === 'textbox') return true;
  return false;
}
export function useStatusShortcuts({ conversationId, enabled, onSnoozeRequest }: Args) {
  const update = useUpdateStatus();
  const navigate = useNavigate();
  useEffect(() => {
    if (!enabled || !conversationId) return;
    const handler = (e: KeyboardEvent) => {
      // Bail on any modifier except Shift (so Shift+1 → '!' still fires).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // Treat Shift+1 as '!'.
      const rawKey = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const lookup = e.shiftKey && rawKey === '1' ? '!' : rawKey;
      const action = KEY_MAP[lookup];
      if (!action) return;
      e.preventDefault();
      if (action === 'snooze') {
        onSnoozeRequest();
        return;
      }
      update.mutate(
        { id: conversationId, status: action },
        { onSuccess: () => navigate('/') },
      );
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [conversationId, enabled, onSnoozeRequest, update, navigate]);
  return update;
}
